'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { cn } from '@/shared/utils/cn';
import { Search, BookOpen, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStories } from '@/contexts/stories';
import { contentKeys } from '@/contexts/content/queryKeys';

const PAGE_SIZE = 5;

interface StorySelectStepProps {
  selectedStoryId: string | null;
  onSelect: (storyId: string, storyTitle: string, blockCount: number, language: string, totalChars?: number) => void;
}

export function StorySelectStep({ selectedStoryId, onSelect }: StorySelectStepProps) {
  const t = useTranslations('content.wizard');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: contentKeys.storySelect(),
    queryFn: () => getStories({ pageSize: 100 }),
  });

  // Stories live in `draft` status indefinitely (the pipeline never flips them
  // to `completed`), so filter by presence of generated text blocks instead.
  // This both excludes still-generating stories and shows everything the user
  // can actually extend.
  const stories = (data?.stories ?? []).filter((s) => (s.textBlocks?.length ?? 0) > 0);

  const filtered = useMemo(() => {
    if (!search.trim()) return stories;
    const term = search.toLowerCase();
    return stories.filter((s) => s.title.toLowerCase().includes(term));
  }, [stories, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedStories = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Auto-select if only one story matches and nothing is selected
  useEffect(() => {
    if (filtered.length === 1 && !selectedStoryId) {
      onSelect(filtered[0].id, filtered[0].title, filtered[0].textBlocks?.length ?? 0, filtered[0].language, (filtered[0].textBlocks ?? []).reduce((n, b) => n + (b.text?.length ?? 0), 0));
    }
  }, [filtered, selectedStoryId, onSelect]);

  return (
    <div data-testid="content-wizard-story-select-step" className="space-y-4">
      <h3 className="text-base font-medium">{t('storySelect.title')}</h3>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="story-select-search"
          placeholder={t('storySelect.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Stories list */}
      {isLoading && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          {t('storySelect.loading')}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <p
          data-testid="story-select-empty"
          className="py-8 text-center text-sm text-muted-foreground"
        >
          {t('storySelect.empty')}
        </p>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="space-y-2">
          {paginatedStories.map((story) => {
            const isSelected = selectedStoryId === story.id;
            return (
              <Card
                key={story.id}
                data-testid={`story-select-item-${story.id}`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => onSelect(story.id, story.title, story.textBlocks?.length ?? 0, story.language, (story.textBlocks ?? []).reduce((n, b) => n + (b.text?.length ?? 0), 0))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(story.id, story.title, story.textBlocks?.length ?? 0, story.language, (story.textBlocks ?? []).reduce((n, b) => n + (b.text?.length ?? 0), 0));
                  }
                }}
                className={cn(
                  'cursor-pointer transition-all hover:border-primary/50',
                  isSelected && 'border-primary ring-2 ring-primary/20',
                )}
              >
                <CardHeader className="flex flex-row items-center gap-3 p-3">
                  <div
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-lg',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <BookOpen className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="truncate text-sm">{story.title}</CardTitle>
                    <CardDescription className="text-xs">
                      {story.language} · {new Date(story.createdAt).toLocaleDateString()}
                    </CardDescription>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="mr-1 size-4" />
                {t('storySelect.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('storySelect.page', { current: page, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('storySelect.next')}
                <ChevronRight className="ml-1 size-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
